import { useMemo } from 'react';
import { AlertTriangle, BarChart3, ListChecks, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OperationalSummaryCards } from './OperationalSummaryCards';

const OPEN_STATUSES = new Set(['pending', 'in_progress', 'correction_requested']);
const innerTabsListClass = 'flex h-auto w-full flex-wrap justify-start gap-4 rounded-none border-b border-slate-200 bg-transparent px-3.5 py-0';
const innerTabsTriggerClass = 'h-9 rounded-none border-b-2 border-transparent px-0 text-xs font-medium uppercase tracking-[0.12em] text-slate-500 shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:text-slate-900';

const alertCardMeta = {
  pending: { label: 'Pendências', icon: AlertTriangle },
  corrections: { label: 'Correcções', icon: ShieldAlert },
  suggestions: { label: 'Sugestões', icon: ListChecks },
  indicators: { label: 'Indicadores', icon: BarChart3 },
};

export const OperationalOverviewTab = ({ workspace, overview, dashboard, catalogSummary, workers }) => {
  const summary = overview?.summary || workspace?.summary || {};
  const overviewRows = overview?.rows || [];
  const workspaceRows = useMemo(() => ([...(workspace?.today || []), ...(workspace?.backlog || [])]), [workspace?.backlog, workspace?.today]);
  const pendingRows = useMemo(() => {
    const sourceRows = overviewRows.length > 0 ? overviewRows : workspaceRows;
    return sourceRows.filter((row) => OPEN_STATUSES.has(row.status));
  }, [overviewRows, workspaceRows]);
  const criticalRows = useMemo(() => pendingRows.filter((row) => ['critical', 'high'].includes(String(row.priority || '').toLowerCase())).slice(0, 6), [pendingRows]);
  const correctionRows = overview?.open_corrections || [];
  const suggestionRows = workspace?.priority_suggestions || [];
  const rankingRows = dashboard?.ranking || [];
  const recurrenceRows = catalogSummary?.recurrences || [];
  const areaRows = catalogSummary?.areas || [];
  const justificationRows = dashboard?.justification_breakdown || [];
  const alertCards = [
    { key: 'pending', value: pendingRows.length, note: 'Itens operacionais ainda abertos.' },
    { key: 'corrections', value: correctionRows.length, note: 'Pedidos aguardando validação.' },
    { key: 'suggestions', value: suggestionRows.length, note: 'Focos críticos sugeridos para o dia.' },
    { key: 'indicators', value: rankingRows.length + recurrenceRows.length, note: 'Sinais executivos activos nesta data.' },
  ];

  return (
    <div className="space-y-2" data-testid="operational-overview-tab">
      <OperationalSummaryCards summary={summary} testIdPrefix="operational-overview-summary" />

      <Tabs defaultValue="summary" className="space-y-2" data-testid="operational-overview-subtabs">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-subtabs-shell">
          <TabsList className={innerTabsListClass} data-testid="operational-overview-subtabs-list">
            <TabsTrigger value="summary" className={innerTabsTriggerClass} data-testid="operational-overview-summary-trigger">Resumo</TabsTrigger>
            <TabsTrigger value="pending" className={innerTabsTriggerClass} data-testid="operational-overview-pending-trigger">Pendências</TabsTrigger>
            <TabsTrigger value="alerts" className={innerTabsTriggerClass} data-testid="operational-overview-alerts-trigger">Alertas</TabsTrigger>
            <TabsTrigger value="indicators" className={innerTabsTriggerClass} data-testid="operational-overview-indicators-trigger">Indicadores</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="summary" className="mt-0" data-testid="operational-overview-summary-content">
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1.2fr)_332px]">
            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-radar-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Radar executivo</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 px-3 py-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-overview-radar-pending">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Pendências</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{pendingRows.length}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Volume aberto no período.</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-overview-radar-corrections">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Correcções</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{correctionRows.length}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Fila de revisão aberta.</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-overview-radar-workers">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Colaboradores</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{(workers || []).length}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Elegíveis nesta data.</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-overview-radar-catalog">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Catálogo</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{catalogSummary?.total ?? 0}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Itens activos disponíveis.</p>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-focus-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Foco imediato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                {criticalRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-focus-empty">Sem pendências críticas ou altas no momento.</p> : criticalRows.map((row) => (
                  <div key={row.id} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`operational-overview-focus-row-${row.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">{row.title}</p>
                      <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{row.priority}</Badge>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{row.assignee_name || row.function_label || 'Sem responsável explícito'} · {row.area || 'Área geral'}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-0" data-testid="operational-overview-pending-content">
          <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-pending-table-card">
            <CardHeader className="border-b border-slate-200 px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-900">Pendências operacionais</CardTitle>
                  <p className="mt-1 text-xs text-slate-500">Vista directa para backlog, bloqueios e itens que exigem seguimento imediato.</p>
                </div>
                <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{pendingRows.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="overflow-auto px-0 pb-0 pt-0">
              <table className="w-full min-w-[900px] text-xs" data-testid="operational-overview-pending-table">
                <thead className="sticky top-0 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  <tr>
                      <th className="px-3 py-2">Data</th>
                    <th className="px-3 py-2">Actividade</th>
                    <th className="px-3 py-2">Área</th>
                    <th className="px-3 py-2">Responsável</th>
                    <th className="px-3 py-2">Prioridade</th>
                    <th className="px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-5 text-sm text-slate-500" data-testid="operational-overview-pending-empty">Sem pendências activas neste momento.</td>
                    </tr>
                  ) : pendingRows.slice(0, 18).map((row) => (
                    <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`operational-overview-pending-row-${row.id}`}>
                      <td className="px-3 py-2">{row.scheduled_date}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.title}</td>
                      <td className="px-3 py-2 text-slate-700">{row.area || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.assignee_name || row.function_label || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.priority || 'medium'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.status_label || row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-0" data-testid="operational-overview-alerts-content">
          <div className="grid gap-3 xl:grid-cols-4">
            {alertCards.map((item) => {
              const Icon = alertCardMeta[item.key].icon;
              return (
                <Card key={item.key} className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`operational-overview-alert-card-${item.key}`}>
                  <CardContent className="flex items-start justify-between gap-3 px-3.5 py-3.5">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{alertCardMeta[item.key].label}</p>
                      <p className="mt-1 text-2xl font-semibold text-slate-950">{item.value}</p>
                      <p className="mt-1 text-[11px] text-slate-500">{item.note}</p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-slate-600"><Icon className="h-4 w-4" /></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-3 grid gap-3 xl:grid-cols-3">
            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-alert-corrections-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Correcções abertas</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {correctionRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-alert-corrections-empty">Sem correcções em fila.</p> : correctionRows.slice(0, 6).map((row) => (
                  <div key={row.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-alert-correction-${row.id}`}>
                    <p className="text-sm font-medium text-slate-900">{row.assignee_name}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{row.request_note}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-alert-suggestions-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Sugestões prioritárias</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {suggestionRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-alert-suggestions-empty">Sem sugestões abertas.</p> : suggestionRows.slice(0, 6).map((item) => (
                  <div key={item.activity_instance_id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-alert-suggestion-${item.activity_instance_id}`}>
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{item.reason}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-alert-justifications-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Justificações em destaque</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {justificationRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-alert-justifications-empty">Sem justificações agregadas.</p> : justificationRows.slice(0, 6).map((item, index) => (
                  <div key={`${item.label}-${index}`} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-alert-justification-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{item.count} ocorrência(s)</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="indicators" className="mt-0" data-testid="operational-overview-indicators-content">
          <div className="grid gap-3 xl:grid-cols-3">
            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-indicators-ranking-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Colaboradores em foco</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {rankingRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-indicators-ranking-empty">Sem ranking disponível.</p> : rankingRows.slice(0, 6).map((item, index) => (
                  <div key={item.worker_id || index} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-indicators-ranking-${index}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.worker_name}</p>
                      <p className="text-[11px] text-slate-500">Pendentes: {item.pending} · backlog: {item.backlog_index}</p>
                    </div>
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">{item.operational_score}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-indicators-recurrences-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Recorrências activas</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {recurrenceRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-indicators-recurrences-empty">Sem recorrências agregadas.</p> : recurrenceRows.map((item) => (
                  <div key={item.key} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-indicators-recurrence-${item.key}`}>
                    <p className="text-sm font-medium text-slate-900">{item.key}</p>
                    <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{item.count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-overview-indicators-areas-card">
              <CardHeader className="border-b border-slate-200 px-3.5 py-2.5"><CardTitle className="text-base font-semibold text-slate-900">Áreas com maior peso</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 py-3.5">
                {areaRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-overview-indicators-areas-empty">Sem áreas agregadas.</p> : areaRows.slice(0, 6).map((item) => (
                  <div key={item.key} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-overview-indicators-area-${item.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{item.area}</p>
                      <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{item.count}</Badge>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{item.sector}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};